/* ==========================================================================
   <shop-the-look> — hotspot previews.
   ≥750px with a hotspot: small anchored panel beside the dot (non-modal dialog,
   Esc / outside click / close button, focus returns to the dot).
   <750px or list-only: native <dialog class="sheet"> via SIM.openDialog.
   Preview content is cloned from <template data-preview="n"> so <product-form>
   and <quick-add-button> upgrade on insertion.
   ========================================================================== */
const SIM = window.SIM || {};

if (!customElements.get('shop-the-look')) {
  class ShopTheLook extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-frame]');
      this.panel = this.querySelector('[data-panel]');
      this.panelBody = this.querySelector('[data-panel-body]');
      this.sheet = this.querySelector('[data-sheet]');
      this.sheetBody = this.querySelector('[data-sheet-body]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.mq = window.matchMedia('(min-width: 750px)');

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-panel-close]')) { this.closePanel(true); return; }
        const trigger = e.target.closest('[data-preview-index]');
        if (!trigger) return;
        e.preventDefault();
        const i = trigger.dataset.previewIndex;
        if (trigger === this.activeDot && !this.panel?.hidden) { this.closePanel(true); return; }
        this.open(i, trigger);
      });

      this.onDocClick = (e) => {
        if (!this.panel || this.panel.hidden) return;
        if (this.panel.contains(e.target) || e.target.closest('[data-preview-index]')) return;
        this.closePanel(false);
      };
      document.addEventListener('click', this.onDocClick);

      this.panel?.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.stopPropagation(); this.closePanel(true); }
      });
      this.onResize = () => { if (this.panel && !this.panel.hidden) this.closePanel(false); };
      this.mq.addEventListener('change', this.onResize);

      // List ↔ dot highlight
      this.querySelectorAll('[data-item]').forEach((item) => {
        const dot = this.dots.find((d) => d.dataset.previewIndex === item.dataset.item);
        if (!dot) return;
        const on = () => dot.classList.add('is-highlighted');
        const off = () => dot.classList.remove('is-highlighted');
        item.addEventListener('pointerenter', on);
        item.addEventListener('pointerleave', off);
        item.addEventListener('focusin', on);
        item.addEventListener('focusout', off);
      });

      // Theme editor: selecting a hotspot block opens its preview
      this.onBlockSelect = (e) => {
        const dot = this.dots.find((d) => d.contains(e.target) || d === e.target);
        if (dot) this.open(dot.dataset.previewIndex, dot);
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      this.mq?.removeEventListener('change', this.onResize);
    }

    content(i) {
      const tpl = this.querySelector(`template[data-preview="${i}"]`);
      return tpl ? tpl.content.cloneNode(true) : null;
    }

    dotVisible(dot) {
      return dot && dot.offsetParent !== null;
    }

    open(i, trigger) {
      const content = this.content(i);
      if (!content) return;
      const dot = this.dots.find((d) => d.dataset.previewIndex === i);
      if (this.mq.matches && this.panel && this.dotVisible(dot)) {
        this.closePanel(false);
        this.panelBody.replaceChildren(content);
        this.panel.hidden = false;
        this.activeDot = dot;
        this.opener = trigger;
        dot.setAttribute('aria-expanded', 'true');
        dot.classList.add('is-active');
        this.position(dot);
        requestAnimationFrame(() => this.panel.classList.add('is-open'));
        this.panel.focus({ preventScroll: true });
      } else if (this.sheet) {
        this.closePanel(false);
        this.sheetBody.replaceChildren(content);
        SIM.openDialog(this.sheet, trigger);
      }
    }

    position(dot) {
      const f = this.frame.getBoundingClientRect();
      const d = dot.getBoundingClientRect();
      const p = this.panel.getBoundingClientRect();
      const gap = 8;
      // Image coordinates are physical (the photo is never mirrored), so use left/top
      let left = d.right - f.left + gap;
      if (left + p.width > f.width) left = d.left - f.left - gap - p.width;
      left = Math.max(gap, Math.min(left, f.width - p.width - gap));
      let top = d.top - f.top + d.height / 2 - p.height / 2;
      top = Math.max(gap, Math.min(top, f.height - p.height - gap));
      this.panel.style.left = `${Math.round(left)}px`;
      this.panel.style.top = `${Math.round(top)}px`;
    }

    closePanel(returnFocus) {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.panel.classList.remove('is-open');
      this.panelBody.replaceChildren();
      if (this.activeDot) {
        this.activeDot.setAttribute('aria-expanded', 'false');
        this.activeDot.classList.remove('is-active');
      }
      if (returnFocus && this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      this.activeDot = null;
    }
  }
  customElements.define('shop-the-look', ShopTheLook);
}
